import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, RefreshCw, History, CheckCircle2, XCircle, SkipForward, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

import { ButtonTemplate } from '../../../components/ui/buttons/ButtonTemplate';
import { useGetBatchAuditQuery, useRetryFailedMutation } from '../api/parserImportApi';

const TABS = [
  { key: 'created', label: 'Created', Icon: Sparkles, className: 'text-greenColor', border: 'border-greenColor' },
  { key: 'enriched', label: 'Enriched', Icon: RefreshCw, className: 'text-purpleColor', border: 'border-purpleColor' },
  { key: 'overwritten', label: 'Overwritten', Icon: RefreshCw, className: 'text-orangeColor', border: 'border-orangeColor' },
  { key: 'skipped', label: 'Skipped', Icon: SkipForward, className: 'text-darkGrayColor', border: 'border-gray-300' },
  { key: 'failed', label: 'Failed', Icon: XCircle, className: 'text-redColor', border: 'border-redColor' },
];

const csvEscape = (v) => {
  if (v == null) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (rows, filename) => {
  const headers = ['parser_advert_id', 'realty_id', 'status', 'title', 'duration_ms', 'error', 'updated_at'];
  const lines = [headers.join(',')].concat(
    rows.map((r) => headers.map((h) => csvEscape(r[h])).join(','))
  );
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// Step 4 — post-import results: 5 tabs, CSV export per tab, retry failed.
export const ResultsStep = ({ flow, token, batch, readOnly = false, onClose }) => {
  const [activeTab, setActiveTab] = useState('created');

  const { data: audit, isLoading: isAuditLoading } = useGetBatchAuditQuery(
    { token, batchId: flow.batchId },
    { skip: !flow.batchId }
  );

  const [retryFailed, { isLoading: isRetrying }] = useRetryFailedMutation();

  const counts = batch?.counts || {};
  const rowsByStatus = useMemo(() => {
    // Audit rows may not exist yet for skipped/failed — fall back to recent_items.
    const recent = batch?.recent_items || [];
    const auditRows = audit?.data || audit || [];
    const merged = [...recent, ...auditRows.map((a) => ({
      parser_advert_id: a.parser_advert_id,
      realty_id: a.realty_id,
      status: a.mode === 'enrich' ? 'enriched' : a.mode === 'overwrite' ? 'overwritten' : a.mode,
      title: a.title,
      duration_ms: a.duration_ms,
      error: null,
      updated_at: a.applied_at || a.updated_at,
    }))];
    // De-dupe by parser_advert_id + status.
    const seen = new Set();
    const deduped = merged.filter((r) => {
      const key = `${r.parser_advert_id}-${r.status}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return {
      created: deduped.filter((r) => r.status === 'created'),
      enriched: deduped.filter((r) => r.status === 'enriched'),
      overwritten: deduped.filter((r) => r.status === 'overwritten'),
      skipped: deduped.filter((r) => r.status === 'skipped'),
      failed: deduped.filter((r) => r.status === 'failed'),
    };
  }, [audit, batch]);

  const activeRows = rowsByStatus[activeTab] || [];

  const handleRetry = async () => {
    if (!flow.batchId) return;
    try {
      const res = await retryFailed({ token, batchId: flow.batchId }).unwrap();
      toast.success(
        `Retry batch #${res.new_batch_id} started (${res.retried_count} records)`
      );
      flow.setBatchId(res.new_batch_id);
    } catch (e) {
      toast.error(e?.data?.message || 'Retry failed');
    }
  };

  const handleExport = () => {
    if (!activeRows.length) {
      toast.warning('Nothing to export for this tab');
      return;
    }
    downloadCsv(
      activeRows,
      `import-batch-${flow.batchId}-${activeTab}.csv`
    );
  };

  return (
    <div data-testid="results-summary-tab" className="flex flex-col gap-4 w-full">
      {/* Summary header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wider text-darkGrayColor">
            Batch #{flow.batchId}
          </span>
          <div className="flex items-center gap-2 text-sm text-blackColor">
            <span
              className={
                'flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ' +
                (batch?.status === 'completed'
                  ? 'bg-greenColor/15 text-greenColor'
                  : batch?.status === 'completed_with_errors'
                  ? 'bg-orangeColor/15 text-orangeColor'
                  : batch?.status === 'cancelled'
                  ? 'bg-lightGrayColor text-darkGrayColor'
                  : batch?.status === 'failed'
                  ? 'bg-redColor/15 text-redColor'
                  : 'bg-purpleColor/15 text-purpleColor')
              }
            >
              <CheckCircle2 size={12} />
              {batch?.status || 'pending'}
            </span>
            <span className="text-darkGrayColor">
              {(counts.created || 0) + (counts.enriched || 0) + (counts.overwritten || 0)}{' '}
              applied / {counts.total || 0} total
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/settings/parser/imports"
            data-testid="results-audit-link"
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-gray-200 text-darkGrayColor hover:bg-lightGrayColor"
          >
            <History size={14} />
            Import history
          </Link>
          <button
            type="button"
            data-testid="results-export-csv-button"
            onClick={handleExport}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-gray-200 text-blackColor hover:bg-lightGrayColor"
          >
            <Download size={14} />
            Export CSV
          </button>
          {!readOnly && (counts.failed || 0) > 0 && (
            <span data-testid="results-retry-failed-button">
              <ButtonTemplate
                text={`Retry ${counts.failed} failed`}
                icon={RefreshCw}
                isIconText={true}
                bgColor="bg-purpleColor border-purpleColor"
                isLoadingData={isRetrying}
                handleClick={handleRetry}
              />
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        {TABS.map((tab) => {
          const isActive = tab.key === activeTab;
          const count = counts[`${tab.key}_count`] ?? counts[tab.key] ?? rowsByStatus[tab.key]?.length ?? 0;
          return (
            <button
              key={tab.key}
              type="button"
              data-testid={`results-${tab.key}-tab`}
              onClick={() => setActiveTab(tab.key)}
              className={
                'flex items-center gap-2 px-3 py-2 text-sm transition-colors border-b-2 -mb-px ' +
                (isActive
                  ? tab.border + ' ' + tab.className + ' font-medium'
                  : 'border-transparent text-darkGrayColor hover:text-blackColor')
              }
            >
              <tab.Icon size={14} />
              {tab.label}
              <span
                className={
                  'text-[10px] font-semibold px-1.5 py-0.5 rounded ' +
                  (isActive ? 'bg-lightGrayColor' : 'bg-lightestGrayColor text-darkGrayColor')
                }
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="max-h-[50vh] overflow-y-auto">
        {isAuditLoading && activeRows.length === 0 ? (
          <div className="flex flex-col gap-1">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-9 rounded bg-lightGrayColor animate-pulse"
              />
            ))}
          </div>
        ) : activeRows.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-sm text-darkGrayColor">
            No records in this bucket
          </div>
        ) : (
          <table data-testid="results-audit-table" className="min-w-full text-sm border-collapse">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                  Parser ID
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                  Realty ID
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                  Title
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                  Duration
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody>
              {activeRows.map((row) => (
                <tr
                  key={`${row.parser_advert_id}-${row.updated_at}`}
                  className="border-b border-gray-100 hover:bg-lightestGrayColor"
                >
                  <td className="px-3 py-2 text-xs text-blackColor">
                    #{row.parser_advert_id}
                  </td>
                  <td className="px-3 py-2 text-xs text-blackColor">
                    {row.realty_id ? `#${row.realty_id}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-blackColor max-w-[400px] truncate">
                    {row.title || '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-darkGrayColor">
                    {row.duration_ms != null ? `${row.duration_ms} ms` : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-redColor max-w-[300px] truncate" title={row.error || ''}>
                    {row.error || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end pt-3 border-t border-gray-200">
        <ButtonTemplate
          text="Done"
          bgColor="bg-purpleColor border-purpleColor"
          handleClick={onClose}
        />
      </div>
    </div>
  );
};
